import React, { useMemo, useCallback } from 'react';

import { LinkElement } from '@els/els-react--link-element';
import useBreakpoint from '@Hooks/useBreakpoint';
import { useBuildInfo } from './useBuildInfo';
import { useFormatBuildInfo } from './useFormatBuildInfo';

import './InfoBar.scss';

const ITEMS_CONFIG = [
  { dataKey: 'substances', icon: 'icon-substances', label: 'Substances' },
  { dataKey: 'reactions', icon: 'icon-reactions', label: 'Reactions' },
  { dataKey: 'citations', icon: 'icon-citations', label: 'Documents' },
  { dataKey: 'patents', icon: 'icon-patents', label: 'Patents' },
  { dataKey: 'dpitems', icon: 'icon-tgitems', label: 'Bioactivities' },
];

function InfoBar() {
  const buildInfo = useBuildInfo();
  const data = useFormatBuildInfo(buildInfo);
  const isDesktop = useBreakpoint('mobile');

  // Memoize items array to prevent unnecessary re-renders
  const items = useMemo(
    () =>
      ITEMS_CONFIG.map((config) => ({
        ...config,
        value: data[config.dataKey] || 0,
      })),
    [data]
  );

  // Memoize render functions
  const renderStatItem = useCallback(
    (item, index, isMobileLayout = false) => {
      const showDivider = isMobileLayout ? index === 0 || index === 2 : index < items.length - 1;

      return (
        <li
          key={item.label}
          className={isMobileLayout ? 'info-bar__mobile-item' : 'u-els-margin-right-1x1o2 o-els-flex-layout o-els-flex-layout--middle'}>
          <h4 className="u-els-display-block u-els-padding-bottom-1o4" aria-label={`${item.value} million ${item.label?.toLowerCase()}`}>
            <strong>{item.value}M</strong> {item.label}
          </h4>
          {showDivider && <span className="circular-divider" />}
        </li>
      );
    },
    [items.length]
  );

  const renderStatsList = useMemo(() => {
    const listClassName = isDesktop ? 'info-bar__desktop-list u-els-display-flex' : 'info-bar__mobile-list';

    return (
      <ul className={listClassName} aria-label="Content statistics">
        {items.map((item, index) => renderStatItem(item, index, !isDesktop))}
      </ul>
    );
  }, [items, isDesktop, renderStatItem]);

  // Memoize the last updated section
  const lastUpdatedSection = useMemo(
    () => (
      <div className="u-els-margin-bottom-1x u-els-line-height-heading">
        <span>Latest content update: </span>
        {!!data.lastUpdated && (
          <LinkElement
            id="latest-update-content"
            href="/#/about-content"
            data-e2e="latest-update-content"
            a11y={{ name: 'Latest content update' }}
            isExternal
            inline>
            {data.lastUpdated}
          </LinkElement>
        )}
      </div>
    ),
    [data.lastUpdated]
  );

  return (
    <div className="info-bar--container">
      {lastUpdatedSection}
      {renderStatsList}
    </div>
  );
}

export default InfoBar;
